import type { Transaction } from "@/apis/gql/types";
import {
  TableActionBarButton,
  TableActionBarRoot,
  TableActionBarRowsSelected,
} from "@/components/shared/data-table/action-bar";
import type { Table as TableType } from "@tanstack/react-table";
import { Check, FileX, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";

type Props = {
  onMatch: () => Promise<void>;
  onMarkAsLost: (bankTxnIds: string[]) => Promise<void>;
  onMarkAsReviewed: (bankTxnIds: string[]) => Promise<void>;
  selectedInvoices: Record<string, boolean>;
  table: TableType<Transaction>;
};

export const TableActionBar = ({
  onMarkAsLost,
  onMarkAsReviewed,
  onMatch,
  selectedInvoices,
  table,
}: Props) => {
  const { t } = useTranslation();

  const selectedTransactions = table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original);

  async function handleMatch() {
    await onMatch();
  }

  async function handleMarkAsLost() {
    const bankTxnIds = selectedTransactions.map(
      (transaction) => transaction.bankTxnId,
    );
    await onMarkAsLost(bankTxnIds);
  }

  async function handleMarkAsReviewed() {
    const bankTxnIds = selectedTransactions.map(
      (transaction) => transaction.bankTxnId,
    );
    await onMarkAsReviewed(bankTxnIds);
  }

  if (selectedTransactions.length === 0) return null;

  return (
    <TableActionBarRoot className="gap-4">
      <TableActionBarRowsSelected table={table} />
      <div className="flex flex-row gap-2">
        <TableActionBarButton
          variant="outline"
          className="gap-2"
          onClick={handleMarkAsLost}
        >
          <FileX className="w-4 h-4" />
          {t("transactions.actions.mark_as_lost")}
        </TableActionBarButton>
        <TableActionBarButton
          variant="outline"
          className="gap-2"
          onClick={handleMarkAsReviewed}
        >
          <Check className="w-4 h-4" />
          {t("transactions.actions.mark_as_reviewed")}
        </TableActionBarButton>
        {selectedInvoices && Object.keys(selectedInvoices).length > 0 && (
          <TableActionBarButton
            variant="outline"
            className="gap-2"
            onClick={handleMatch}
          >
            <Link2 className="w-4 h-4" />
            {t("transactions.actions.match")}
          </TableActionBarButton>
        )}
      </div>
    </TableActionBarRoot>
  );
};
